// ═══════════════════════════════════════════════════════════════════════════
// Dynamic Blocks — Geometric Constraints (DYN-3 Tier 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Geometric constraints differ fundamentally from dimensional (parameter)
// constraints:
//   - Dimensional constraints (constraint-horizontal / vertical / angular /
//     aligned / radial / diameter) expose a numeric value via the
//     Properties Palette and drive Move / Stretch / Scale / Array / Rotate
//     actions. They ARE parameters.
//   - Geometric constraints are BINARY RELATIONS between entities — "these
//     two lines must stay parallel", "this point must stay on that line",
//     "this circle must be tangent to that arc". They have NO numeric
//     value and are NOT driven by actions. They are consumed by the
//     constraint SOLVER (DYN-3 B) to restore geometric invariants after
//     action-driven transformations.
//
// For DYN-3 Tier 3 (non-solver parts) we ship the SCHEMA and VALIDATION
// only. The Gauss-Newton solver that actually enforces the constraints is
// authored in parallel in packages/block-evaluator/src/constraintSolver.ts
// by Subagent DYN-3 B — this module intentionally exports no runtime
// solver code.
//
// Storage location: `dynamicBlockMetadataSchema.geometricConstraints` —
// an ordered array of `geometricConstraintSchema` records. Round-trip
// through DXF preserves the raw order (AutoCAD evaluates constraints in
// the order they were authored).

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// Kind enumeration — 12 geometric constraint types AutoCAD supports
// ═══════════════════════════════════════════════════════════════════════════
//
// Order mirrors AutoCAD's "Parametric" panel on the ribbon (Geometric group).
// The comments list the minimum arity (entities involved) for each kind —
// validateGeometricConstraint enforces these.
//
//   coincident        | 2 points (entity subselectors select which endpoint)
//   collinear         | ≥2 lines / line-like entities
//   concentric        | ≥2 circles / arcs sharing one center
//   fix               | 1 entity (point, line, etc.) pinned in place
//   parallel          | exactly 2 lines (parallel direction vectors)
//   perpendicular     | exactly 2 lines (perpendicular direction vectors)
//   tangent           | exactly 2 curves (line/curve or curve/curve)
//   equal             | 2 entities, matching length (lines) or radius (arcs)
//   symmetric         | 2 entities mirrored about `referenceLineId`
//   smooth            | 2 curves with tangent (G1) continuity at endpoint
//   horizontal        | exactly 1 line (alternate to horizontal dim. constr.)
//   vertical          | exactly 1 line (alternate to vertical dim. constr.)
export const geometricConstraintKindSchema = z.enum([
  "coincident",
  "collinear",
  "concentric",
  "fix",
  "parallel",
  "perpendicular",
  "tangent",
  "equal",
  "symmetric",
  "smooth",
  "horizontal",
  "vertical",
]);
export type GeometricConstraintKind = z.infer<
  typeof geometricConstraintKindSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// Geometric constraint record
// ═══════════════════════════════════════════════════════════════════════════
//
// `entityIds` — ordered list of block-entity ids involved in the constraint
// (indexed into blockDef.entities).
//
// `entitySubselectors` — optional ordered list (same length as entityIds)
// holding a string that disambiguates WHICH point / subfeature of the entity
// participates in the constraint. Empty string or undefined = entity as a
// whole. Well-known values (non-exhaustive — DXF preserves anything):
//   - Line       : "start", "end", "midpoint"
//   - Arc        : "start", "end", "center", "midpoint"
//   - Circle     : "center"
//   - Ellipse    : "center", "majorEnd", "minorEnd"
//   - Polyline   : "v0", "v1", … (vertex index)
//
// `referenceLineId` — only meaningful for "symmetric" (id of the mirror
// axis line entity). Other kinds ignore this field.
export const geometricConstraintSchema = z.object({
  id: z.string().min(1),
  kind: geometricConstraintKindSchema,
  entityIds: z.array(z.string()).min(1),
  entitySubselectors: z.array(z.string()).optional(),
  referenceLineId: z.string().optional(),
});
export type GeometricConstraint = z.infer<typeof geometricConstraintSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Per-kind arity validation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Required arity (number of entities) per constraint kind. Numbers are
 * minimums; `Infinity` means "any ≥ N". Used by validateGeometricConstraint
 * to flag author errors early.
 */
const ARITY_MIN: Record<GeometricConstraintKind, number> = {
  coincident: 2,
  collinear: 2,
  concentric: 2,
  fix: 1,
  parallel: 2,
  perpendicular: 2,
  tangent: 2,
  equal: 2,
  symmetric: 2,
  smooth: 2,
  horizontal: 1,
  vertical: 1,
};

/**
 * Max arity per constraint kind. Some (collinear / concentric / coincident)
 * accept unbounded entities; most are exactly 2 (parallel / perpendicular /
 * tangent / symmetric / smooth / equal). Fix / horizontal / vertical are
 * exactly 1.
 */
const ARITY_MAX: Record<GeometricConstraintKind, number> = {
  coincident: Number.POSITIVE_INFINITY,
  collinear: Number.POSITIVE_INFINITY,
  concentric: Number.POSITIVE_INFINITY,
  fix: 1,
  parallel: 2,
  perpendicular: 2,
  tangent: 2,
  equal: 2,
  symmetric: 2,
  smooth: 2,
  horizontal: 1,
  vertical: 1,
};

/**
 * Validate a single GeometricConstraint against a set of known entity ids.
 * Returns an array of error strings (empty when valid). Checks:
 *   - arity (kind-specific min/max)
 *   - entityIds reference ids present in `knownEntityIds`
 *   - entitySubselectors length matches entityIds when provided
 *   - referenceLineId (for symmetric) points to a known entity
 */
export function validateGeometricConstraint(
  c: GeometricConstraint,
  knownEntityIds: ReadonlySet<string>,
): string[] {
  const errors: string[] = [];
  const minA = ARITY_MIN[c.kind];
  const maxA = ARITY_MAX[c.kind];

  if (c.entityIds.length < minA) {
    errors.push(
      `Geometric constraint ${c.id} kind=${c.kind} requires ≥${minA} entities, got ${c.entityIds.length}`,
    );
  }
  if (c.entityIds.length > maxA) {
    errors.push(
      `Geometric constraint ${c.id} kind=${c.kind} accepts ≤${maxA} entities, got ${c.entityIds.length}`,
    );
  }

  for (const eid of c.entityIds) {
    if (!knownEntityIds.has(eid)) {
      errors.push(
        `Geometric constraint ${c.id} references unknown entity ${eid}`,
      );
    }
  }

  if (
    c.entitySubselectors !== undefined &&
    c.entitySubselectors.length !== c.entityIds.length
  ) {
    errors.push(
      `Geometric constraint ${c.id} entitySubselectors length (${c.entitySubselectors.length}) ≠ entityIds length (${c.entityIds.length})`,
    );
  }

  if (c.kind === "symmetric") {
    if (!c.referenceLineId) {
      errors.push(
        `Geometric constraint ${c.id} kind=symmetric requires referenceLineId`,
      );
    } else if (!knownEntityIds.has(c.referenceLineId)) {
      errors.push(
        `Geometric constraint ${c.id} referenceLineId ${c.referenceLineId} unknown`,
      );
    }
  }

  return errors;
}
